import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User as UserIcon, Eye, EyeOff, UserPlus, AlertCircle, Loader2, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { useAuth, api } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Register = () => {
  const { t } = useTranslation();
  const { register } = useAuth();
  const [sp] = useSearchParams();
  const next = sp.get('next');
  const zugangscode = sp.get('code');
  if (zugangscode) localStorage.setItem('kp_zugangscode', zugangscode.toUpperCase());
  const loginLink = next ? `/login?next=${encodeURIComponent(next)}` : '/login';

  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const pwScore = form.password.length >= 12 ? 3 : form.password.length >= 8 ? 2 : form.password.length > 0 ? 1 : 0;
  const pwColors = ['bg-gray-200', 'bg-rose-400', 'bg-amber-400', 'bg-emerald-500'];
  const pwLabels = ['', t('register.password_strength.weak'), t('register.password_strength.medium'), t('register.password_strength.strong')];

  const [gesendet, setGesendet] = useState('');
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) return setError(t('register.errors.password_min_length'));
    if (form.password !== form.confirm) return setError(t('register.errors.passwords_mismatch'));
    if (!agree) return setError(t('register.errors.accept_privacy'));
    setLoading(true);
    try {
      await register(form.email, form.password, form.name);
      setGesendet(form.email);
    } catch (err) {
      setError(err.response?.data?.detail || t('register.errors.registration_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const fieldClass =
    'w-full pl-11 pr-4 py-3 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none focus:ring-2 focus:ring-[#b8893d]/20 text-sm rounded-sm';

  if (gesendet) return (
    <div className="min-h-screen bg-[#f5f8fa]">
    <Header />
    <div className="pt-[190px] pb-20 flex items-center justify-center px-4" data-testid="register-bestaetigung">
      <div className="max-w-md w-full bg-white border border-gray-200 p-8 rounded-sm">
        <h1 className="font-serif text-2xl text-[#1B3A5C] mb-3">Bitte E-Mail bestätigen</h1>
        <p className="text-sm text-[#34536b] leading-relaxed">Wir haben einen Bestätigungslink an <b>{gesendet}</b> gesendet. Nach der Bestätigung können Sie sich anmelden. Die Kanzlei schaltet Ihr Konto anschließend für Ihre Mandantendaten frei.</p>
        <button type="button" onClick={() => api.post('/auth/register/erneut-senden', { email: gesendet })} className="mt-5 text-xs tracking-wider text-[#b8893d] underline" data-testid="register-erneut-senden">Link erneut senden</button>
        <div className="mt-6"><Link to={loginLink} className="text-sm text-[#1B3A5C] underline" data-testid="register-zum-login">Zur Anmeldung</Link></div>
      </div>
    </div>
    <Footer />
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-[160px] pb-20 bg-[#f5f8fa] min-h-[80vh]">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white p-8 lg:p-10 rounded-sm shadow-sm border-l-4 border-[#b8893d]">
            <div className="text-center mb-8">
              <img src="/assets/emblem.png" alt="" aria-hidden="true" className="h-20 w-auto mx-auto mb-4 select-none" draggable="false" data-testid="auth-emblem" />
              <div className="inline-flex items-center gap-2 text-[#b8893d] text-xs tracking-[0.3em] mb-3">
                <span className="w-10 h-px bg-[#b8893d]"></span>
                {t('register.header.client_area')}
                <span className="w-10 h-px bg-[#b8893d]"></span>
              </div>
              <h1 className="font-serif text-3xl text-[#1B3A5C]">{t('register.header.title')}</h1>
              <p className="text-[#34536b] text-sm mt-2">{t('register.header.subtitle')}</p>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 mb-5 bg-rose-50 border-l-4 border-rose-500 text-rose-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#34536b]" />
                <input
                  required
                  placeholder={t('register.form.name_placeholder')}
                  value={form.name} data-testid="register-name-input"
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={fieldClass}
                />
              </div>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#34536b]" />
                <input data-testid="register-email-input"
                  type="email"
                  required
                  placeholder={t('register.form.email_placeholder')}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={fieldClass}
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#34536b]" />
                <input data-testid="register-password-input"
                  type={showPw ? 'text' : 'password'}
                  required
                  placeholder={t('register.form.password_placeholder')}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className={`${fieldClass} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#34536b] hover:text-[#b8893d]"
                  aria-label={showPw ? t('register.form.hide_password') : t('register.form.show_password')}
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {form.password && (
                <div>
                  <div className="flex gap-1">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className={`h-1 flex-1 rounded ${i <= pwScore ? pwColors[pwScore] : 'bg-gray-200'}`} />
                    ))}
                  </div>
                  <p className="text-xs text-[#34536b] mt-1">{t('register.password_strength.label')}: {pwLabels[pwScore]}</p>
                </div>
              )}
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#34536b]" />
                <input
                  type={showPw ? 'text' : 'password'}
                  required
                  placeholder={t('register.form.confirm_password_placeholder')}
                  value={form.confirm} data-testid="register-password-repeat-input"
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                  className={`${fieldClass} pr-11`}
                />
                {form.confirm && form.confirm === form.password && (
                  <Check className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
                )}
              </div>
              <label className="flex items-start gap-2 text-sm text-[#34536b] cursor-pointer">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[#b8893d]"
                />
                <span>
                  {t('register.form.accept_terms_prefix')} <a href="#datenschutz" className="text-[#b8893d] underline">{t('register.form.privacy_policy')}</a> {t('register.form.accept_terms_and')} <a href="#agb" className="text-[#b8893d] underline">{t('register.form.terms')}</a>.
                </span>
              </label>

              <Button data-testid="register-submit-button"
                type="submit"
                disabled={loading}
                className="w-full bg-[#1B3A5C] hover:bg-[#0f2740] text-white tracking-[0.15em] text-xs py-6"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <UserPlus className="mr-2 w-4 h-4" /> {t('register.form.submit_button')}
                  </>
                )}
              </Button>
            </form>

            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="text-xs text-[#34536b]/60 tracking-wider">{t('register.divider.or')}</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>

            <button
              type="button"
              onClick={handleGoogle}
              className="w-full flex items-center justify-center gap-3 py-3 bg-white border-2 border-gray-200 hover:border-[#b8893d] rounded-sm text-sm font-medium text-[#1B3A5C] transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('register.google_button')}
            </button>

            <div className="mt-8 text-center text-sm text-[#34536b]">
              {t('register.login_prompt')}{' '}
              <Link to={loginLink} className="text-[#b8893d] hover:underline font-medium">
                {t('register.login_link')}
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Register;
